'use client'

import { motion } from 'framer-motion'
import { HomeIcon, Globe, Lock, CheckCircle } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
}

interface BenefitCardProps {
  Icon: LucideIcon
  title: string
  description: string
}

const BenefitCard: React.FC<BenefitCardProps> = ({
  Icon,
  title,
  description,
}) => (
  <motion.div variants={fadeIn} className="h-full">
    <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md transition-transform duration-300 hover:-translate-y-2 h-full">
      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4 sm:mb-6">
        <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
      </div>
      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
        {title}
      </h3>
      <p className="text-sm sm:text-base text-gray-700">{description}</p>
    </div>
  </motion.div>
)

const benefitItems: BenefitCardProps[] = [
  {
    Icon: HomeIcon,
    title: 'Unmatched Convenience & Comfort',
    description:
      'Receive expert care from your home, office, or any private location, saving travel time and fitting appointments more easily into your busy schedule.',
  },
  {
    Icon: Globe,
    title: 'Increased Accessibility',
    description:
      'Overcome geographical barriers, mobility limitations, or transportation challenges to access specialized psychiatric services.',
  },
  {
    Icon: Lock,
    title: 'Privacy & Confidentiality',
    description:
      'Our telehealth platform is HIPAA-compliant and secure, ensuring your personal information and session content remain confidential.',
  },
  {
    Icon: CheckCircle,
    title: 'Effective & Evidence-Based',
    description:
      'Research shows that online psychiatry is as effective as in-person care for many mental health conditions, delivering comparable outcomes.',
  },
]

const OnlinePsychiatryBenefits: React.FC = () => (
  <section className="py-12 sm:py-16 lg:py-20 px-4">
    <div className="max-w-7xl mx-auto">
      <motion.div
        className="text-center mb-10 sm:mb-16"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={fadeIn}
      >
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-gray-900 mb-4">
          Benefits of Choosing Online Psychiatry with ReACH
        </h2>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={staggerContainer}
      >
        {benefitItems.map((item) => (
          <BenefitCard key={item.title} {...item} />
        ))}
      </motion.div>
    </div>
  </section>
)

export default OnlinePsychiatryBenefits
